import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { produce } from "immer";
import { chunk, sortBy, uniq } from "lodash";

import {
  GraphQLAPI,
  GraphQLResult,
  gql,
  parseSearchResults,
  queries,
} from "@examspark/api";
import {
  collectAllPages,
  getAscendantObjectiveScopes,
  objectiveCompareFn,
} from "@examspark/common";
import {
  BucketAggregation,
  Course,
  IDSK,
  Mastery,
  ModelObjectiveConnection,
  Objective,
  ObjectivesView,
  ParsedSearchableResourceConnectionNew,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { createObjectivePathKeys } from "../../utils/objectives";
import { createQuery } from "../util";

const createQueryObjective = (id?: string, sk?: string) => {
  return createQuery({
    queryKey: ["objective", id, sk],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return null;

      const result = (await GraphQLAPI.query({
        query: queries.objective.getObjective,
        variables: {
          id: id,
          sk: sk ?? "standard",
        },
      })) as GraphQLResult<{
        getObjective: Objective;
      }>;

      return produce(result.data?.getObjective, (draft) => draft);
    },
  });
};

export const useQueryGetObjective = (id?: string, sk?: string) => {
  return useQueriesGetObjectives([{ id, sk }])[0];
};

export const useQueriesGetObjectives = (
  keys: Array<{ id?: string; sk?: string }>,
) => {
  return useQueries({
    queries: keys.map(({ id, sk }) => createQueryObjective(id, sk)),
  });
};

export const useQueryGetObjectivePath = (id?: string) => {
  const standardQuery = useQueryGetObjective(id, "standard");

  const { data } = standardQuery;

  const scopePath = useQueryGetObjectiveScopePath(data?.scope ?? undefined);

  return {
    standardQuery,
    ...scopePath,
  };
};

export const useQueryGetObjectiveScopePath = (scope?: string) => {
  const { authorityKey, publicationKey, documentKey, sectionKey } =
    createObjectivePathKeys(scope);

  const [authorityQuery, publicationQuery, documentQuery, sectionQuery] =
    useQueriesGetObjectives([
      authorityKey,
      publicationKey,
      documentKey,
      sectionKey,
    ]);

  return {
    authorityQuery,
    publicationQuery,
    documentQuery,
    sectionQuery,
  };
};

export const useQueriesGetObjectiveScopePaths = (scopes?: string[]) => {
  const keys: IDSK[] = [];

  for (const scope of scopes ?? []) {
    const { authorityKey, publicationKey, documentKey, sectionKey } =
      createObjectivePathKeys(scope);
    keys.push(authorityKey, publicationKey, documentKey, sectionKey);
  }

  const results = useQueries({
    queries: keys?.map(({ id, sk }) => createQueryObjective(id, sk)),
  });

  const scopeResults = chunk(results, 4);

  return scopeResults.map((r) => ({
    authorityQuery: r[0],
    publicationQuery: r[1],
    documentQuery: r[2],
    sectionQuery: r[3],
  }));
};

export const useQueryGetObjectiveUsages = (id?: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["objective-usages", id],
    keepPreviousData: true,
    enabled: !!id,
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        filter: {
          alignmentIds: {
            eq: id,
          },
          sk: {
            eq: "unit#v0",
          },
        },
        limit: 50,
        // limit: limit,
        // nextToken: params?.nextToken,
        // sort: [{
        //   field: params?.sort ?? SearchableResourceSortableFields.UpdatedAt,
        //   direction: SearchableSortDirection.Desc,
        // }],
      };

      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "SearchAlignments",
          fields: `
            id
            sk
            idInc
            name
            description
            type
            bankId
            workspaceId
            createdAt
            updatedAt
            grade
            subject
          `,
        }),
        variables,
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      // console.log("next", next.aggregations);
      return result.data?.search;
    },
  });
};

export const useQueryGetCourseObjectives = ({
  courseId,
  sk,
}: {
  courseId?: string;
  sk: `course#v0`;
}) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["course-objectives", courseId],
    enabled: !!courseId && !!sk,
    queryFn: async () => {
      if (!courseId) return null;

      const result = (await GraphQLAPI.query({
        query: queries.classroom.getCourseObjectives,
        variables: {
          input: {
            id: courseId,
            sk: sk,
          },
          authorizationContext,
        },
      })) as GraphQLResult<{
        getCourse: Course;
      }>;

      return result.data?.getCourse ?? null;
    },
  });
};

export const useQueryListObjectives = (scope: string | undefined) => {
  return useQuery<Objective[]>({
    enabled: !!scope,
    queryKey: ["objectives", scope],
    queryFn: async () => {
      const results = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.objective.listObjectivesByScopeSequence,
          variables: {
            scope: scope,
            nextToken,
          },
        })) as GraphQLResult<{
          listObjectivesByScopeSequence: ModelObjectiveConnection;
        }>;

        return result.data?.listObjectivesByScopeSequence;
      });

      return results.flatMap((r) => r.items ?? []);
    },
  });
};

export const useQuerySearchObjectives = (scopes: string[] | undefined) => {
  const sortedScopes = sortBy(scopes) ?? [];
  return useQuery<Objective[]>({
    enabled: sortedScopes.length > 0,
    queryKey: ["objectives", "search", ...sortedScopes],
    queryFn: () => gql.operations.objective.searchObjectives(scopes ?? []),
  });
};

export const useQueryGetMastery = (params: {
  classroomId?: string;
  unitId?: string;
  studentId?: string;
}) => {
  const { classroomId, unitId, studentId } = params;
  const getObjective = useMutationGetObjective();
  const authorizationContext = useAuthorizationContext();

  const filter: SearchableResourceFilterInput = {
    sk: {
      wildcard: "exam-response*",
    },
    isArchived: {
      ne: true,
    },
    isDeleted: {
      ne: true,
    },
    and: [],
  };

  if (classroomId) {
    filter.and?.push({
      tags: {
        eq: `classroom-id#${classroomId}`,
      },
    });
  }

  if (unitId) {
    filter.and?.push({
      tags: {
        eq: `classroom-unit-id#${unitId}`,
      },
    });
  }

  if (studentId) {
    filter.and?.push({
      tags: {
        eq: `classroom-student-id#${studentId}`,
      },
    });
  }

  return useQuery({
    queryKey: ["classroom", classroomId, "mastery", unitId, studentId],
    enabled: !!classroomId,
    queryFn: async () => {
      const results = (await GraphQLAPI.query<{
        search: ParsedSearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          fields: `
            id
            idInc
            idVendor
            alignments {
              items {
                id
                scope
                abbreviation
              }
            }
            resource {
              id
              sk
              idInc
              name
              description
            }
          `,
        }),
        variables: {
          authorizationContext,
          filter,
          size: 0,
          aggsNew: [
            {
              name: "creatorId",
              type: "terms",
              field: "creatorId",
            },
            {
              name: "examConfigId",
              type: "terms",
              field: "examConfigId",
            },
            {
              name: "alignmentIds",
              type: "terms",
              field: "alignmentIds",
              aggs: [
                {
                  name: "examSessionCount",
                  type: "cardinality",
                  field: "examSessionId",
                },
                {
                  name: "responsesOverTime",
                  type: "dateHistogram",
                  field: "createdAt",
                  interval: "1w",
                  extendedBounds: {
                    min: new Date(
                      // 12 weeks ago
                      Date.now() - 1000 * 60 * 60 * 24 * 7 * 12,
                    ).toISOString(),
                    max: new Date().toISOString(),
                  },
                  aggs: [
                    {
                      name: "maxPoints",
                      type: "sum",
                      field: "scoreMax",
                    },
                    {
                      name: "actualPoints",
                      type: "sum",
                      field: "scoreActual",
                    },
                  ],
                },
              ],
            },
          ],
        },
      })) as GraphQLResult<{ search: ParsedSearchableResourceConnectionNew }>;

      const parsedResult = parseSearchResults(results.data?.search);
      const buckets = parsedResult.aggregations?.alignmentIds.buckets;
      const _objectiveIds = parsedResult.aggregations?.alignmentIds.buckets.map(
        (obj: BucketAggregation) => ({
          id: obj.key,
        }),
      );

      const objectives = await Promise.all(
        _objectiveIds.map((obj: { id: string }) =>
          getObjective.mutateAsync(obj.id),
        ),
      );

      // console.log("objectives", _objectiveIds, objectives);
      const _mastery: Mastery[] = buckets.map((obj: any) => ({
        key: obj.key,
        doc_count: obj.examSessionCount?.value || 0,
        objective: objectives.find((a) => a.id === obj.key),
        dates: obj.responsesOverTime.buckets.map((w: any) => ({
          key: w.key,
          doc_count: w.doc_count,
          scoreMax: w.maxPoints.value,
          scoreActual: w.actualPoints.value,
        })),
      })) as Mastery[];

      return _mastery.sort((a, b) =>
        objectiveCompareFn(a.objective, b.objective),
      );
    },
  });
};

export const useQueryGetObjectivesView = (id?: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery<ObjectivesView>({
    enabled: !!id,
    queryKey: ["objectives-view", id],
    queryFn: async () => {
      const result = (
        (await GraphQLAPI.query({
          query: queries.objective.getObjectivesView,
          variables: {
            input: {
              id: id,
              sk: "objectives-view#v0",
            },
            authorizationContext,
          },
        })) as any
      ).data.getObjectivesView;

      if (result.data) {
        result.data = JSON.parse(result.data);
      }

      return result;
    },
  });
};

export const useQueryGetObjectivesViews = (
  objectiveScopes: string[] | null | undefined,
  bankId: string | null | undefined,
) => {
  const authorizationContext = useAuthorizationContext();

  const allObjectiveScopes = uniq(
    objectiveScopes?.flatMap((scope) => getAscendantObjectiveScopes(scope)),
  );

  return useQuery<ObjectivesView[]>({
    enabled: !!objectiveScopes,
    queryKey: ["objectives-views", bankId, ...(objectiveScopes ?? [])],
    queryFn: async () =>
      (
        (await GraphQLAPI.query({
          query: queries.search.search({
            label: "GetObjectivesViews",
            fields: `id sk name type`,
          }),
          variables: {
            filter: {
              sk: {
                eq: "objectives-view#v0",
              },
              bankId: {
                eq: bankId,
              },
              or: allObjectiveScopes?.map((scope) => ({
                objectiveScope: { eq: scope },
              })),
            },
            authorizationContext,
          } as QuerySearchArgs,
        })) as any
      ).data.search.items,
  });
};

export const useMutationGetObjective = () => {
  return useMutation({
    mutationFn: async (id: string, sk?: string) => {
      if (!id) return null;

      const result = (await GraphQLAPI.query({
        query: queries.objective.getObjective,
        variables: {
          id: id,
          sk: sk ?? "standard",
        },
      })) as GraphQLResult<{
        getObjective: Objective;
      }>;

      return produce(result.data?.getObjective, (draft) => draft);
    },
  });
};
